<template>
  <div v-if="!!session" class="session-view-container">
    <header class="view-header has-text-centered">
      <div class="view-header-container has-text-centered">
        <div class="session-details-title columns is-marginless is-variable is-4 is-vcentered pr-2">
          <div class="column is-paddingless">
            <div v-if="isNestedSession" class="is-flex is-flex-direction-row is-align-items-center" @click="openParentSessionDetailsModal">
              <div class="session-details-small-text is-clickable">{{ $t("agenda.belongs_to_parent") }}</div>
              <div class="chain-icon-container is-clickable">
                <chain-icon class="svg-icon has-fill-primary-color-text"></chain-icon>
              </div>
              <div class="session-details-small-text has-text-weight-bolder is-clickable">
                {{ parentSession.name }}
              </div>
            </div>

            <div class="mt-1">
              {{ agendaPlace.value }}
            </div>
          </div>
          <b-button
            @click.stop="openLiveStreamLink"
            type="is-outlined"
            class="button agenda-session-live-button column is-narrow is-paddingless"
            :class="{ 'has-live-tag': liveIndicatorVisible }"
            v-if="liveIndicatorVisible"
          >
            <div class="agenda-session-live-button-content">
              <span>
                {{ $t("common.join") }}
              </span>
              <span v-if="liveIndicatorVisible">{{ ` ${$t("common.live")}` }}</span>
              <stream-icon class="stream-icon has-text-primary"></stream-icon>
            </div>
            <div class="live-tag" v-if="liveIndicatorVisible">
              <div class="live-stream-blob"></div>
              <div class="live-text">
                {{ $t("common.live") }}
              </div>
            </div>
          </b-button>
        </div>

        <div class="session-details-subtitle">
          <div>{{ title }}</div>
        </div>
      </div>

      <div class="column is-gapless">
        <div class="media-left has-text-right" v-if="!hideSessionTime">
          <div class="agenda-session-date">{{ formatDate(startTime) }}</div>
          <div class="agenda-details-view-start-time">{{ formatTime(startTime) }}</div>
          <div class="agenda-details-view-end-time">{{ formatTime(endTime) }}</div>
        </div>
      </div>
    </header>

    <section class="modal-card-body pt-1 modal-card-foot">
      <hr class="mt-0 mb-1" />
      <div class="agenda-session-path-tags-container">
        <agenda-session-path-tag :path-tag="agendaPath" v-if="isScientificTheme"></agenda-session-path-tag>
      </div>
      <div class="content agenda-section agenda-description">
        <p v-if="description" v-html="description"></p>
      </div>

      <div class="agenda-section" v-if="sessionChildren && sessionChildren.length">
        <div class="is-clickable" v-for="nestedSession in sessionChildren" :key="`session-details-nested-${session.id}-${nestedSession.id}`">
          <agenda-session
              @open="openChildDetails(nestedSession.id)"
              :session="nestedSession"
              :session-ratings="sessionRatings"
              :hide-session-time="hideSessionTime"
              :show-live-indicator="showLiveIndicator"
              :hide-live-stream-buttons="hideLiveStreamButtons"
              :is-nested="true"
          ></agenda-session>
        </div>
      </div>

      <div v-if="attachments.length > 0" class="agenda-section" style="width: 100%">
        <h3 class="title is-4 mb-2">{{ $t("agenda.title_attached_files") }}</h3>
        <attachment-list class="is-fullwidth" :files="files" :links="links" column-class="is-full"></attachment-list>
      </div>

      <template>
        <div class="columns">
          <div class="column" :class="speakersAttendeesColumnClass">
            <div v-if="speakers.length > 0" class="agenda-section">
              <speaker-list :session="session" :column-class="speakersColumnClass"></speaker-list>
            </div>
          </div>
          <div class="column" :class="speakersAttendeesColumnClass" v-if="customSponsorVisible">
            <session-sponsor-support :sponsor-with-support="customSponsor"></session-sponsor-support>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import SessionSponsorSupport from "@/widgets/agenda/components/custom/SessionSponsorSupport";
import AgendaSessionPathTag from "@/widgets/agenda/components/sessions/AgendaSessionPathTag";
import { mapGetters, mapState } from "vuex";
import { composeAgendaSessionRatingTime, composeAgendaSessionTime, composeDayDate } from "@/shared/utils";
import Constants from "@/widgets/shared/constants";
import AttachmentList from "@/widgets/agenda/components/sessions/AttachmentList";
import SpeakerList from "@/widgets/agenda/components/sessions/SpeakerList";
import StreamIcon from "@/assets/icon_live_stream.svg";
import ChainIcon from "@/assets/icon_chain.svg";
import AgendaSession from "@/widgets/agenda/components/sessions/AgendaSession";

export default {
  name: "SessionDetailsView",
  components: {
    AgendaSession,
    SpeakerList,
    SessionSponsorSupport,
    AgendaSessionPathTag,
    AttachmentList,
    StreamIcon,
    ChainIcon,
  },
  props: {
    agendaSessionId: {
      type: Number,
      required: false,
    },
  },

  data() {
    return {
      forcedAgendaSessionId: null,
    };
  },

  methods: {
    openLiveStreamLink() {
      if (this.liveUrl) {
        window.open(this.liveUrl, "_blank");
      }
    },

    openParentSessionDetailsModal() {
      this.forcedAgendaSessionId = this.parentSession.id;
    },

    openChildDetails(childSessionId) {
      this.forcedAgendaSessionId = childSessionId;
    },

    formatTime(date) {
      return composeAgendaSessionTime(new Date(date).getTime(), this.timezone);
    },

    formatDate(date) {
      return composeDayDate(new Date(date).getTime(), this.timezone);
    },
  },

  computed: {
    ...mapGetters("agendaSessionTickets", ["hasTicketForSession"]),
    ...mapGetters("schedule", ["isAttendingSession"]),
    ...mapGetters("agendaSessionAttendees", ["attendeesFromSession", "attendeesCountFromSession", "isLoading"]),
    ...mapState("agendaSessionAttendees", { agendaSessionAttendeesError: "error" }),
    ...mapGetters("agendaSessionRatings", ["getRating", "getRatingError"]),
    ...mapGetters("sessions", ["sessionById"]),
    ...mapState([
      "timezone",
      "constantComponentId",
      "componentIdsWithRatingAlwaysEnabled",
      "token",
      "lpMode",
      "currentTime",
      "theme",
      "hideSessionTime",
      "showLiveIndicator",
      "hideLiveStreamButtons",
      "sessionRatings",
      "customSponsor",
    ]),

    id() {
      return this.forcedAgendaSessionId || this.agendaSessionId;
    },

    session() {
      return this.sessionById(this.id);
    },

    agendaComponentId() {
      return this.session.event_component_id;
    },

    startTime() {
      return this.session.time_start;
    },

    endTime() {
      return this.session.time_end;
    },

    title() {
      return this.session.name;
    },

    subtitle() {
      return this.session.tag_agenda_place.value;
    },

    agendaPlace() {
      return this.session.tag_agenda_place;
    },

    agendaPath() {
      return this.session.tag_agenda_path;
    },

    agendaPathColor() {
      return this.agendaPath.hex;
    },

    description() {
      return this.session.description && this.$options.filters.linkify(this.session.description);
    },

    liveUrl() {
      return this.session.video_call_url;
    },

    isAttending() {
      return this.isAttendingSession(this.session);
    },

    requiresTicket() {
      return this.session.agenda_session_tickets.length;
    },

    sessionTicket() {
      return this.requiresTicket && this.session.agenda_session_tickets[0];
    },

    ticketsLeft() {
      return this.sessionTicket && this.sessionTicket.tickets_left;
    },

    hasTicket() {
      return this.hasTicketForSession(this.session.id);
    },

    isNestedSession() {
      return !!this.session.agenda_session_parent_id;
    },

    parentSession() {
      return this.isNestedSession ? this.sessionById(this.session.agenda_session_parent_id) : null;
    },

    sessionChildren() {
      return this.session.agenda_session_children;
    },

    speakers() {
      return [...this.session.speakers].sort((a, b) => {
        if (a.vip !== b.vip) {
          return a.vip ? -1 : 1;
        } else {
          return a.order - b.order;
        }
      });
    },

    files() {
      return this.session.session_attachments;
    },

    links() {
      return this.session.agenda_session_external_links;
    },

    attachments() {
      return [...this.files, ...this.links];
    },

    attendees() {
      return this.attendeesFromSession(this.id);
    },

    attendeesTotalCount() {
      return this.attendeesCountFromSession(this.id);
    },

    areAttendeesLoading() {
      return this.isLoading(this.id);
    },

    attendButtonVisible() {
      return this.lpMode && !this.session.attend_button_hidden && !this.constantComponentId;
    },

    attendeesVisible() {
      return this.token && this.lpMode && !this.constantComponentId;
    },

    liveIndicatorVisible() {
      if (this.currentTime && this.showLiveIndicator) {
        return new Date(this.startTime) < this.currentTime && this.currentTime < new Date(this.endTime);
      } else {
        return false;
      }
    },

    streamButtonVisible() {
      return !this.hideLiveStreamButtons && this.liveUrl;
    },

    sessionDiscussionVisible() {
      return this.lpMode && this.session.agenda_discussion_on;
    },

    ratingEnabled() {
      if (this.componentIdsWithRatingAlwaysEnabled) {
        if (this.componentIdsWithRatingAlwaysEnabled.some(componentId => componentId === this.agendaComponentId)) {
          return true;
        }
      }
      return new Date(this.startTime) < this.currentTime;
    },

    ratingVisible() {
      return this.lpMode && this.session.ratings_visible;
    },

    ratingObject() {
      return this.getRating(this.id);
    },

    ratingValue() {
      return this.ratingObject && this.ratingObject.rating;
    },

    ratingTime(date) {
      const timestamp = new Date(this.startTime).getTime();
      return composeAgendaSessionRatingTime(timestamp, this.timezone);
    },

    ratingReviews() {
      return this.ratingObject && this.ratingObject.reviews;
    },

    ratingError() {
      return this.getRatingError(this.id);
    },

    customSponsorVisible() {
      return !!this.customSponsor;
    },

    speakersAttendeesColumnClass() {
      if (this.customSponsorVisible) {
        return "is-half";
      } else {
        return "";
      }
    },

    speakersColumnClass() {
      if (this.customSponsorVisible) {
        return "is-full";
      } else {
        return "is-half";
      }
    },

    isDefaultTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.DEFAULT;
    },

    isScientificTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },
  },
};
</script>

<style scoped>

.session-view-container {
  border-radius: 6px;
  width: 100%;

  & .view-header {
    align-items: start;
    background: var(--primary-color);
    padding: 24px;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    display: flex;
    align-items: center;
  }

  & .agenda-session-date {
    color: #fff;
  }

  & .agenda-details-view-start-time {
    color: #fff;
    font-weight: 600;
  }

  & .agenda-details-view-end-time {
    color: #f6f6f6;
    font-weight: 600;
  }

  & .modal-card-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: start;
  }

  & .columns {
    width: 100%;
  }

  & .content {
    white-space: pre-wrap;
  }

  & .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
  }

  & .pointer-hover:hover {
    cursor: pointer;
  }

  & .button-text {
    font-size: 18px;
    font-weight: bolder;
  }

  & .agenda-description {
    font-size: 14px;
  }

  & .chain-icon-container {
    height: 10px;
    width: 20px;
    margin-left: 4px;
    margin-right: 4px;
  }
}
</style>
